import React, { useEffect, useRef } from "react";

// 'speed' represents the duration in seconds to loop once (singleWidth).
// Higher number = Slower speed (e.g., 60 = 60 seconds to loop once).
const PortfolioScroller = ({ children, speed = 60 }) => {
  const wrapperRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftStartRef = useRef(0);
  const lastTimeRef = useRef(0);
  const scrollPosRef = useRef(0);
  const pauseTimeoutRef = useRef(null);

  // Flatten children to map them easily
  const originalItems = React.Children.toArray(children);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Set initial scroll position to the start of the middle set
    const initializeScroll = () => {
      const singleWidth = wrapper.scrollWidth / 3;
      if (singleWidth > 0) {
        wrapper.scrollLeft = singleWidth;
        scrollPosRef.current = singleWidth;
      }
    };

    // Delay slightly to ensure layout and images are loaded
    const timer = setTimeout(initializeScroll, 100);

    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const elapsed = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      if (!isPausedRef.current && !isDraggingRef.current) {
        const singleWidth = wrapper.scrollWidth / 3;
        if (singleWidth > 0) {
          const speedPixelsPerMs = singleWidth / (speed * 1000);
          scrollPosRef.current += speedPixelsPerMs * elapsed;
          
          if (scrollPosRef.current >= 2 * singleWidth) {
            scrollPosRef.current -= singleWidth;
          }
          wrapper.scrollLeft = scrollPosRef.current;
        }
      } else {
        // Sync scroll position reference when manually scrolled/dragged
        scrollPosRef.current = wrapper.scrollLeft;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      clearTimeout(timer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [speed]);

  const handleScroll = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const singleWidth = wrapper.scrollWidth / 3;
    if (singleWidth <= 0) return;

    if (wrapper.scrollLeft >= 2 * singleWidth) {
      wrapper.scrollLeft -= singleWidth;
      scrollPosRef.current = wrapper.scrollLeft;
    } else if (wrapper.scrollLeft <= singleWidth) {
      wrapper.scrollLeft += singleWidth;
      scrollPosRef.current = wrapper.scrollLeft;
    } else {
      scrollPosRef.current = wrapper.scrollLeft;
    }
  };

  const handlePrev = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    isPausedRef.current = true;
    clearTimeout(pauseTimeoutRef.current);

    const cardOffset = 352; // 320px width + 32px margin
    wrapper.scrollBy({ left: -cardOffset, behavior: "smooth" });

    // Resume auto-scroll after 3 seconds of inactivity
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 3000);
  };

  const handleNext = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    isPausedRef.current = true;
    clearTimeout(pauseTimeoutRef.current);

    const cardOffset = 352; // 320px width + 32px margin
    wrapper.scrollBy({ left: cardOffset, behavior: "smooth" });

    // Resume auto-scroll after 3 seconds of inactivity
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 3000);
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    isDraggingRef.current = true;
    startXRef.current = e.pageX - wrapper.offsetLeft;
    scrollLeftStartRef.current = wrapper.scrollLeft;
    isPausedRef.current = true;
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const x = e.pageX - wrapper.offsetLeft;
    const walk = (x - startXRef.current) * 1.5; // Drag speed multiplier
    wrapper.scrollLeft = scrollLeftStartRef.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      document.body.style.userSelect = "";
      
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = setTimeout(() => {
        isPausedRef.current = false;
      }, 3000);
    }
  };

  return (
    <div 
      className="pf-scroll-container" 
      style={{ position: "relative", width: "100%" }}
      onMouseEnter={() => {
        if (!isDraggingRef.current) isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        if (!isDraggingRef.current) isPausedRef.current = false;
      }}
    >
      {/* Navigation Buttons (positioned outside/relative to the scrollable wrapper) */}
      <button 
        className="pf-scroll-btn pf-scroll-btn-left" 
        onClick={(e) => {
          e.stopPropagation();
          handlePrev();
        }}
        aria-label="Previous"
      >
        <i className="fas fa-chevron-left"></i>
      </button>

      <button 
        className="pf-scroll-btn pf-scroll-btn-right" 
        onClick={(e) => {
          e.stopPropagation();
          handleNext();
        }}
        aria-label="Next"
      >
        <i className="fas fa-chevron-right"></i>
      </button>

      {/* Scrollable Wrapper */}
      <div
        className="pf-scroll-wrapper"
        ref={wrapperRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
          padding: "20px 0",
          cursor: "grab",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <div
          className="pf-scroll-track"
          style={{
            display: "flex",
            width: "max-content",
            willChange: "transform",
            animation: "none", // Overrides CSS animations from Portfolio.css
          }}
        >
          {/* RENDER 3 SETS FOR SEAMLESS LOOP */}
          {[0, 1, 2].map((setIndex) => (
            <React.Fragment key={`set-${setIndex}`}>
              {originalItems.map((child, i) =>
                React.cloneElement(child, {
                  key: `set${setIndex}-${i}`,
                  style: {
                    ...child.props.style,
                    marginRight: "32px",
                  }
                })
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <style>{`
        .pf-scroll-wrapper::-webkit-scrollbar {
          display: none;
        }
        .pf-scroll-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(26, 26, 26, 0.7);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(248, 200, 220, 0.3);
          color: #f8c8dc;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
          z-index: 20; /* High z-index to overlay on top of cards */
        }
        .pf-scroll-btn-left {
          left: 20px;
        }
        .pf-scroll-btn-right {
          right: 20px;
        }
        .pf-scroll-btn:hover {
          background: #f8c8dc;
          color: #1a1a1a;
          border-color: #f8c8dc;
          box-shadow: 0 0 20px rgba(248, 200, 220, 0.8);
        }
        .pf-scroll-btn-left:hover {
          transform: translateY(-50%) scale(1.1);
        }
        .pf-scroll-btn-right:hover {
          transform: translateY(-50%) scale(1.1);
        }
        .pf-scroll-btn:active {
          transform: translateY(-50%) scale(0.95);
        }
        .pf-scroll-wrapper:active {
          cursor: grabbing;
        }
        
        /* Hide buttons on mobile/touch screen viewports */
        @media (max-width: 768px) {
          .pf-scroll-btn {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default PortfolioScroller;